import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Image, Form, Button, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Formik, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import InputField from "../../../components/input/input-field";
import avatars1 from "../../../assets/images/avatars/01.png";
import CustomPhoneInput from "../../../components/input/phone-input";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import "react-phone-input-2/lib/style.css";
import MultiSelect from "../../../components/input/multi-select";
import Select from "react-select";
import { defaultPermissions } from "../../../data/default-permission";
import { roleTypes } from "../../../data/roleType";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const rolePermissions = {
  [roleTypes.ADMIN]: [
    "receiving",
    "purchase_order",
    "product_request",
    "job",
    "rack_management",
    "system",
    "phase",
    "vendor",
    "manufacturer",
    "general_product_category",
    "general_product",
    "warehouse",
    "location",
    "stakeholder",
    "user",
  ],
  [roleTypes.SUPPLIER]: [
    "product_request",
    "purchase_order",
    "receiving",
  ],
  [roleTypes.PURCHASING]: [
    "product_request",
    "purchase_order",
    "receiving",
  ],
  [roleTypes.WAREHOUSE]: [
    "product_request",
    "rack_management",
    "warehouse",
    "system",
    "phase",
    "user",
    "receiving",
    "job",
    "location",
    "vendor",
    "stakeholder",
  ],
  [roleTypes.TECHNICIAN]: [
    "product_request",
    "rack_management",
    "warehouse",
    "system",
    "phase",
    "receiving",
    "job",
    "location",
    "vendor",
    "stakeholder",
  ],
};

const WarehouseAccountAdd = () => {
  const navigate = useNavigate();
  const alertRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const token = localStorage.getItem("coyote-token");
  const [decodedToken, setDecodedToken] = useState();
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    profilePicture: null,
    password: "",
    confirmPassword: "",
    warehouseId: null,
    warehouseRoles: [],
    userPermissions: [],
  });

  const permissionsWarehouse = useSelector(SettingSelector?.set_permissions);
  const warehouseHasCreatePermission =
    permissionsWarehouse?.warehouse?.create === true;
  const getPermissions = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}permission/active-user-permissions`,
        config
      );
      const allPermissions = response.data.response.data;

      setPermissions(allPermissions);

      const initialPermissions = allPermissions.reduce((acc, permission) => {
        acc[permission.slug] = permission.permissions.reduce(
          (obj, perm) => ({ ...obj, [perm.permission_slug]: false }),
          {}
        );
        return acc;
      }, {});

      setFormData({
        ...formData,
        userPermissions: initialPermissions,
      });
    } catch (err) {
      handleError(err, "account-add");
    }
  };

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type)
      )
    ) {
      const getRoles = async () => {
        try {
          const config = {
            headers: {
              Authorization: `${token}`,
            },
          };
          const response = await axios.get(
            `${url.backendURL}warehouse/role/all`,
            config
          );
          setRoles(response.data.response.data);
        } catch (err) {
          handleError(err, "roles");
        }
      };
      getRoles();
      setWarehouses([
        {
          id: decodedToken?.warehouse_id,
          name: decodedToken?.warehouse,
        },
      ]);
      getPermissions();
    }
  }, [decodedToken]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
          ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (
      decodedToken?.roles.some((role) =>
        [roleTypes.WAREHOUSE].includes(role.role_type)
      )
    ) {
      setSubmitting(true);
      const data = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "warehouseRoles" || key === "userPermissions") {
          data.append(key, JSON.stringify(values[key]));
        } else if (key !== "profilePicture" && key !== "warehouseId") {
          data.append(key, values[key]);
        }
      });
      data.append("warehouseId", values.warehouseId || "");
      const fileInput = document.getElementById("profilePicture");
      if (values.profilePicture) {
        const file = fileInput.files[0];
        data.append("profilePicture", file);
      }
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "multipart/form-data",
          },
        };

        const response = await axios.post(
          `${url.backendURL}warehouse/user`,
          data,
          config
        );
        if (alertRef.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        setErrors({});
        setAlert({
          message: response.data.message,
          type: "success",
          show: true,
          alertFor: "user-submit",
        });
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
          navigate(`/dashboard/app/warehouse/user-list`);
        }, 2000);
      } catch (err) {
        handleError(err, "user-submit");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      profilePicture: Yup.mixed()
        .test(
          "fileType",
          "Image must be a valid file (jpeg, png, jpg)",
          (value) =>
            !value ||
            (value &&
              ["image/jpeg", "image/png", "image/jpg"].includes(value.type))
        )
        .nullable(),
      warehouseRoles: Yup.array()
        .of(Yup.string().required("Role is required"))
        .min(1, "At least one role is required")
        .required("Roles are required"),
      warehouseId: Yup.number()
        .integer("Warehouse is invalid")
        .positive("Warehouse is required")
        .required("Warehouse is required"),

      name: Yup.string()
        .min(1, "Name must be at least 1 character long")
        .max(255, "Name must not exceed 255 characters")
        .trim()
        .matches(
          /^(?=.*[A-Za-z])[A-Za-z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/,
          "Name must start with a letter and can include numbers, spaces, but cannot be entirely numbers"
        )
        .required("Name is required"),

      email: Yup.string()
        .trim()
        .required("Email is required")
        .email("Enter a valid email address")
        .test(
          "no-spaces",
          "Email may not contain any spaces at the beginning or end",
          (value) => !value || value.trim() === value
        )
        .test("min-domain-segments", "Enter a valid email address", (value) => {
          if (!value) return true;
          const parts = value.split("@");
          if (parts.length !== 2) return false;
          const domainSegments = parts[1].split(".");
          return (
            domainSegments.length >= 2 &&
            domainSegments.every((s) => s.length >= 2)
          );
        })
        .test("realistic-email", "Enter a valid email address", (value) => {
          if (!value) return true;
          const [local, domain] = value.split("@");
          if (!local || !domain) return false;

          if (/^\d+$/.test(local)) return false;
          if (local.length > 64) return false;

          if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

          return true;
        }),

      phoneNumber: Yup.string()
        .trim()
        .matches(/^[0-9+\-]{10,15}$/, "Phone number must be 10 to 15 digits")
        .optional()
        .nullable()
        .test(
          "trim",
          "Phone number may not contain any spaces at the beginning or end",
          (value) => value === (value && value.trim())
        ),

      password: Yup.string()
        .min(6, "Password must be at least 6 characters long")
        .max(30, "Password cannot be longer than 30 characters")
        .matches(
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{6,}$/,
          "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
        )
        .required("Password is required"),

      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),

      userPermissions: Yup.object()
        .shape(
          permissions.reduce((acc, permission) => {
            acc[permission.slug] = Yup.object().shape(
              permission.permissions.reduce((innerAcc, perm) => {
                innerAcc[perm.permission_slug] = Yup.boolean();
                return innerAcc;
              }, {})
            );
            return acc;
          }, {})
        )
        .test(
          "at-least-one-permission",
          "At least one permission must be selected",
          function (value) {
            const warehouseRoles = this.parent.warehouseRoles;
            if (!warehouseRoles || warehouseRoles.length === 0) {
              return true;
            }
            if (!value) return false;

            const isAnySelected = Object.values(value).some(
              (entityPermissions) =>
                entityPermissions &&
                Object.values(entityPermissions).some(
                  (action) => action === true
                )
            );
            return isAnySelected;
          }
        ),
    });

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const roleOptions =
      roles.length > 0 &&
      roles.map((role) => ({
        value: role.id,
        label: role.name,
        roleType: role.role_type || role.roleType,
      }));

    setOptions(roleOptions);
  }, [roles]);

  const [warehouseOptions, setWarehouseOptions] = useState([]);

  useEffect(() => {
    const warehouseOptions =
      warehouses.length > 0 &&
      warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
        defaultHead: warehouse.default_head,
      }));
    setWarehouseOptions(warehouseOptions || []);
  }, [warehouses]);

  const scrollToFirstError = (errors, touched) => {
    const firstErrorKey = Object.keys(errors).find((key) => {
      if (typeof errors[key] === "object" && errors[key] !== null) {
        return Object.keys(errors[key]).some(
          (nestedKey) => touched[key] && touched[key][nestedKey]
        );
      }
      return touched[key];
    });

    if (firstErrorKey) {
      let targetElement =
        document.querySelector(`[name="${firstErrorKey}"]`) ||
        document.querySelector(`#${firstErrorKey}`) ||
        document.querySelector(`[name*="${firstErrorKey}"]`);

      if (!targetElement && typeof errors[firstErrorKey] === "object") {
        const nestedErrorKey = Object.keys(errors[firstErrorKey])[0];
        targetElement =
          document.querySelector(
            `[name="${firstErrorKey}.${nestedErrorKey}"]`
          ) || document.querySelector(`[name*="${firstErrorKey}"]`);
      }

      if (!targetElement) {
        const errorElement = document.querySelector(".text-danger");
        if (errorElement) {
          targetElement =
            errorElement.closest(".form-group, .mb-3, .col") || errorElement;
        }
      }

      if (targetElement?.type === "hidden") {
        const fallbackElement =
          document.getElementById(firstErrorKey) ||
          document.querySelector(`#${firstErrorKey}-anchor`) ||
          document.querySelector(".text-danger");
        if (fallbackElement) targetElement = fallbackElement;
      }

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        if (targetElement.focus && typeof targetElement.focus === "function") {
          setTimeout(() => targetElement.focus(), 300);
        }
      }
    }
  };

  const getPermissionsForRoles = (selectedRoleIds, rolesOptions) => {
    if (!selectedRoleIds || !rolesOptions || selectedRoleIds.length === 0)
      return [];

    const allPermissions = new Set();

    selectedRoleIds.forEach((roleId) => {
      const role = rolesOptions.find((opt) => opt.value === roleId);
      if (role && rolePermissions[role.roleType]) {
        rolePermissions[role.roleType].forEach((permission) => {
          allPermissions.add(permission);
        });
      }
    });

    return Array.from(allPermissions);
  };

  const [isSubmittingButton, setIsSubmittingButton] = useState(false);

  return (
    <>
      <div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...formData,
            warehouseId: decodedToken?.warehouse_id,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          onInvalidSubmit={(errors, touched) => {
            setTimeout(() => {
              scrollToFirstError(errors, touched);
            }, 50);
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            setFieldValue,
            handleSubmit,
            touched,
            isSubmitting,
            setFieldError,
            setFieldTouched,
            errors: errorsformik,
          }) => {
            useEffect(() => {
              if (isSubmittingButton && Object.keys(errorsformik).length > 0) {
                scrollToFirstError(errorsformik, touched);
              }
              setIsSubmittingButton(false);
            }, [errorsformik, isSubmittingButton, isSubmitting, touched]);

            return (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsSubmittingButton(true);
                  handleSubmit();
                }}
              >
                <Row>
                  <Col xl="3" lg="4" className="d-flex">
                    <div style={{ width: "100%", display: "grid" }}>
                      <Card>
                        <Card.Header className="d-flex justify-content-between">
                          <div className="header-title">
                            <h4 className="card-title">Add User</h4>
                          </div>
                        </Card.Header>

                        <Card.Body>
                          <Form.Group className="form-group">
                            <div className="profile-img-edit position-relative">
                              <Image
                                className="theme-color-default-img profile-pic rounded avatar-100"
                                src={
                                  values.profilePicture
                                    ? URL.createObjectURL(values.profilePicture)
                                    : avatars1
                                }
                                alt="profile-pic"
                              />

                              <div
                                className="upload-icone bg-primary"
                                onClick={() =>
                                  document
                                    .getElementById("profilePicture")
                                    .click()
                                }
                              >
                                <svg
                                  className="upload-button"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="#ffffff"
                                    d="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                  />
                                </svg>
                                <Form.Control
                                  className="file-upload"
                                  id="profilePicture"
                                  type="file"
                                  accept=".png, .jpg, .jpeg"
                                  name="profilePicture"
                                  onChange={(e) =>
                                    setFieldValue(
                                      "profilePicture",
                                      e.currentTarget.files[0]
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="img-extension mt-3">
                              <div className="d-inline-block align-items-center">
                                <span>Only</span> <Link to="#">.jpg</Link>{" "}
                                <Link to="#">.png</Link>{" "}
                                <Link to="#">.jpeg</Link> <span>allowed</span>
                              </div>
                            </div>
                            <ErrorMessage
                              name="profilePicture"
                              component="div"
                              className="text-danger"
                            />
                            {errors?.profilePicture?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>

                          <Form.Group className="form-group">
                            <Form.Label htmlFor="warehouseRoles">
                              Roles<span className="text-danger">*</span>
                            </Form.Label>
                            <MultiSelect
                              name="warehouseRoles"
                              id="warehouseRoles"
                              options={options}
                              isMulti={true}
                              isClearable={false}
                              value={values.warehouseRoles}
                              onChange={(selectedRoles) => {
                                setErrors((prev) => ({ ...prev, roles: undefined, warehouseRoles: undefined }));
                                let updatedRoles = [...selectedRoles];

                                setFieldValue("warehouseRoles", updatedRoles);
                                setTimeout(() => {
                                  setFieldTouched("warehouseRoles", true, false);
                                  if (updatedRoles && updatedRoles.length > 0) {
                                    setFieldError("warehouseRoles", undefined);
                                  } else {
                                    setFieldError("warehouseRoles", "At least one role is required");
                                    setFieldError("userPermissions", undefined);
                                  }
                                }, 50);

                                let mergedPermissions = {
                                  ...values.userPermissions,
                                };

                                Object.keys(mergedPermissions).forEach(
                                  (entitySlug) => {
                                    Object.keys(
                                      mergedPermissions[entitySlug]
                                    ).forEach((action) => {
                                      mergedPermissions[entitySlug][
                                        action
                                      ] = false;
                                    });
                                  }
                                );

                                updatedRoles.forEach((roleValue) => {
                                  const roleOption = options.find(
                                    (option) => option.value === roleValue
                                  );

                                  const rolePermissionsFromDefault = roleOption
                                    ? defaultPermissions.find(
                                      (rolePermission) =>
                                        rolePermission.roleType ===
                                        roleOption.roleType
                                    )
                                    : null;

                                  if (rolePermissionsFromDefault) {
                                    rolePermissionsFromDefault.predefinedPermissions.forEach(
                                      ({ entity, actions }) => {
                                        if (entity === "user") {
                                          return;
                                        }

                                        if (
                                          mergedPermissions[entity] &&
                                          rolePermissions[
                                          roleOption.roleType
                                          ] &&
                                          rolePermissions[
                                            roleOption.roleType
                                          ].includes(entity)
                                        ) {
                                          actions.forEach((action) => {
                                            if (
                                              mergedPermissions[entity][
                                              action
                                              ] !== undefined
                                            ) {
                                              mergedPermissions[entity][
                                                action
                                              ] = true;
                                              if (
                                                (roleOption.roleType ===
                                                  roleTypes.ADMIN ||
                                                  roleOption.roleType ===
                                                  roleTypes.SUPER_ADMIN) &&
                                                entity === "warehouse"
                                              ) {
                                                mergedPermissions["warehouse"][
                                                  "create"
                                                ] = true;
                                                mergedPermissions["warehouse"][
                                                  "delete"
                                                ] = true;
                                              }
                                            }
                                          });
                                        }
                                      }
                                    );
                                  }
                                });

                                setFieldValue(
                                  "userPermissions",
                                  mergedPermissions
                                );
                              }}
                              onBlur={handleBlur}
                              placeholder="Select role(s)..."
                            />

                            <ErrorMessage
                              name="warehouseRoles"
                              component="div"
                              className="text-danger"
                            />
                            {errors?.warehouseRoles?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>

                          <Form.Group className="form-group">
                            <Form.Label htmlFor="warehouseId">
                              Warehouse
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                              closeMenuOnScroll={(e) => {
                                try {
                                  const target = e.target;

                                  if (
                                    !target ||
                                    !target.nodeType ||
                                    typeof target.closest !== "function"
                                  ) {
                                    return true;
                                  }

                                  const isMenuScroll =
                                    target.closest('[class*="MenuList"]') ||
                                    target.closest('[class*="menu"]');

                                  return !isMenuScroll;
                                } catch (error) {
                                  return true;
                                }
                              }}
                              name="warehouseId"
                              id="warehouseId"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              options={warehouseOptions}
                              isMulti={false}
                              value={warehouseOptions.find(
                                (option) => option.value === values.warehouseId
                              )}
                              onChange={(selectedOption) => {
                                const newWarehouseId = selectedOption
                                  ? selectedOption.value
                                  : null;

                                setFieldValue("warehouseId", newWarehouseId);
                                const selectedWarehouse =
                                  warehouseOptions?.find(
                                    (wh) => wh.value === newWarehouseId
                                  );

                                if (
                                  !selectedWarehouse ||
                                  selectedWarehouse.defaultHead !== 0
                                ) {
                                  const updatedPermissions = {
                                    ...values.userPermissions,
                                  };

                                  if (updatedPermissions["user"]) {
                                    Object.keys(
                                      updatedPermissions["user"]
                                    ).forEach((action) => {
                                      updatedPermissions["user"][
                                        action
                                      ] = false;
                                    });

                                    setFieldValue(
                                      "userPermissions",
                                      updatedPermissions
                                    );
                                  }
                                } else {
                                  const updatedPermissions = {
                                    ...values.userPermissions,
                                  };

                                  const hasWarehouseHeadRole =
                                    values.warehouseRoles?.some((roleValue) => {
                                      const role = options.find(
                                        (opt) => opt.value === roleValue
                                      );
                                      return (
                                        role?.label?.toLowerCase() ===
                                        "warehouse-head" &&
                                        role?.roleType === roleTypes.WAREHOUSE
                                      );
                                    });

                                  if (
                                    hasWarehouseHeadRole &&
                                    updatedPermissions["user"]
                                  ) {
                                    const warehouseHeadDefaultPermissions =
                                      defaultPermissions.find(
                                        (rolePermission) =>
                                          rolePermission.roleType ===
                                          roleTypes.WAREHOUSE
                                      );

                                    if (warehouseHeadDefaultPermissions) {
                                      const userEntityPermissions =
                                        warehouseHeadDefaultPermissions.predefinedPermissions.find(
                                          (perm) => perm.entity === "user"
                                        );

                                      if (userEntityPermissions) {
                                        Object.keys(
                                          updatedPermissions["user"]
                                        ).forEach((action) => {
                                          updatedPermissions["user"][action] =
                                            userEntityPermissions.actions.includes(
                                              action
                                            );
                                        });

                                        setFieldValue(
                                          "userPermissions",
                                          updatedPermissions
                                        );
                                      }
                                    }
                                  }
                                }
                              }}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  color: "#5e667b",
                                }),
                                placeholder: (provided) => ({
                                  ...provided,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }),
                              }}
                              onBlur={handleBlur}
                              noOptionsMessage={({ inputValue }) => (
                                <div
                                  style={{
                                    textAlign: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span style={{ color: "#666" }}>
                                    {warehouseOptions.length === 0 ? (
                                      <>
                                        No warehouse found.
                                        {(warehouseHasCreatePermission ||
                                          decodedToken?.roles.some(
                                            (role) =>
                                              role.role_type ===
                                              roleTypes.SUPER_ADMIN,
                                          )) && (
                                            <span>
                                              {" "}
                                              <Link
                                                to="/dashboard/app/warehouse-add"
                                                style={{
                                                  color: "#007bff",
                                                  textDecoration: "none",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                Add new
                                              </Link>
                                            </span>
                                          )}
                                      </>
                                    ) : (
                                      "No warehouse found"
                                    )}
                                  </span>
                                </div>
                              )}
                              placeholder={
                                warehouseOptions.length === 0 ? (
                                  <span>No warehouse found</span>
                                ) : (
                                  "Select Warehouse..."
                                )
                              }
                            />
                            <ErrorMessage
                              name="warehouseId"
                              component="div"
                              className="text-danger"
                            />
                            {errors?.warehouseId?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>

                  <Col xl="9" lg="8" ref={alertRef} className="d-flex">
                    <Card>
                      <Card.Header className="d-flex justify-content-between">
                        <div
                          className="breadcrumb header-title"
                          style={{ cursor: "pointer" }}
                        >
                          <h4
                            onClick={() =>
                              navigate("/dashboard/app/warehouse/user-list")
                            }
                            className="text-primary card-title"
                            style={{ cursor: "pointer" }}
                          >
                            <span>User</span>
                          </h4>
                          <h4 className="mx-2 card-title">{">"}</h4>
                          <h4 className="card-title">User Information</h4>
                        </div>
                      </Card.Header>

                      <Card.Body>
                        <AlertNotificationSimple
                          type={alert.type}
                          message={alert.message}
                          show={alert.show}
                          onClose={() => setAlert({ ...alert, show: false })}
                        />

                        <div className="new-user-info">
                          <div className="row">
                            <InputField
                              className="col-md-6 form-group"
                              type="text"
                              name="name"
                              placeholder="Enter Name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              errorMessage={errors?.name}
                              label="Name"
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="email"
                              name="email"
                              placeholder="Email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              errorMessage={errors?.email}
                              label="Email"
                            />

                            <Row>
                              <Form.Group className="col-md-6 form-group">
                                <Form.Label htmlFor="phoneNumber">
                                  Phone Number
                                </Form.Label>
                                <CustomPhoneInput
                                  id="phoneNumber"
                                  name="phoneNumber"
                                  value={values.phoneNumber}
                                  onChange={(value) =>
                                    setFieldValue("phoneNumber", value)
                                  }
                                  onBlur={handleBlur}
                                  placeholder="Enter phone number"
                                  country="us"
                                  isValid={(value, country) => {
                                    if (value.match(/12345/)) {
                                      return `Invalid value: ${value}, ${country.name}`;
                                    } else if (value.match(/1234/)) {
                                      return false;
                                    } else {
                                      return true;
                                    }
                                  }}
                                />
                                <ErrorMessage
                                  name="phoneNumber"
                                  component="div"
                                  className="text-danger"
                                />
                                {errors?.phoneNumber?.map((error, index) => (
                                  <span
                                    key={index}
                                    className="w-100 mt-1 small text-danger"
                                  >
                                    {error}
                                    <br />
                                  </span>
                                ))}
                              </Form.Group>
                            </Row>

                            <InputField
                              className="col-md-6 form-group"
                              type="password"
                              name="password"
                              placeholder="Password"
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showPasswordToggle={true}
                              showPassword={showPassword}
                              setShowPassword={setShowPassword}
                              asterick={true}
                              errorMessage={errors?.password}
                              label="Password"
                            />

                            <InputField
                              className="col-md-6 form-group"
                              type="password"
                              name="confirmPassword"
                              placeholder="Confirm Password"
                              value={values.confirmPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showPasswordToggle={true}
                              showPassword={showConfirmPassword}
                              setShowPassword={setShowConfirmPassword}
                              asterick={true}
                              errorMessage={errors?.confirmPassword}
                              label="Confirm Password"
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Row>
                  <Col xl="12" lg="12">
                    <Card>
                      <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                          <h4
                            className="card-title"
                            id="userPermissions-anchor"
                          >
                            Permissions
                          </h4>
                        </div>
                      </Card.Header>

                      <Card.Body>
                        <div className="new-user-info">
                          {errorsformik.userPermissions &&
                            touched.userPermissions &&
                            typeof errorsformik.userPermissions ===
                            "string" && (
                              <div className="text-danger small mb-3">
                                {errorsformik.userPermissions}
                              </div>
                            )}
                          {permissions
                            .filter((permission) => {
                              if (
                                !values.warehouseRoles ||
                                values.warehouseRoles.length === 0
                              ) {
                                return false;
                              }

                              if (permission.slug === "user") {
                                const hasWarehouseHeadRole =
                                  values.warehouseRoles?.some((roleValue) => {
                                    const role = options.find(
                                      (opt) => opt.value === roleValue
                                    );
                                    return (
                                      role?.label?.toLowerCase() ===
                                      "warehouse-head" &&
                                      role?.roleType === roleTypes.WAREHOUSE
                                    );
                                  });

                                if (!hasWarehouseHeadRole) {
                                  return false;
                                }

                                if (values.warehouseId) {
                                  const selectedWarehouse =
                                    warehouseOptions?.find(
                                      (wh) => wh.value === values.warehouseId
                                    );

                                  if (
                                    !selectedWarehouse ||
                                    selectedWarehouse.defaultHead !== 0
                                  ) {
                                    return false;
                                  }
                                } else {
                                  return false;
                                }
                              }

                              const allowedPermissions = getPermissionsForRoles(
                                values.warehouseRoles,
                                options
                              );

                              return allowedPermissions.includes(
                                permission.slug
                              );
                            })
                            .map((permission, index) => {
                              return (
                                options.length > 0 && (
                                  <div key={index} className="mb-4">
                                    <Form.Label
                                      htmlFor="userPermissions"
                                      className="text-black"
                                      name="userPermissions"
                                    >
                                      {permission.label}
                                    </Form.Label>
                                    <div className="d-flex gap-3 flex-wrap">
                                      {permission.permissions
                                        .filter((perm) => {
                                          const normalizedEntity = String(permission.slug || "").toLowerCase().trim();
                                          const normalizedAction = String(perm.permission_slug || "").toLowerCase().trim();

                                          if (normalizedEntity === "warehouse" && (normalizedAction === "create" || normalizedAction === "delete")) {
                                            const selectedRoleIds = (values.warehouseRoles || []).map(r => (r && typeof r === 'object') ? r.value : r);
                                            const hasAdminRole = options.some(opt =>
                                              selectedRoleIds.some(id => String(id) === String(opt.value)) &&
                                              (opt.roleType === roleTypes.ADMIN || opt.roleType === roleTypes.SUPER_ADMIN)
                                            );
                                            return !!hasAdminRole;
                                          }
                                          return perm.display === true;
                                        })
                                        .map((perm) => (
                                          <React.Fragment
                                            key={perm.permission_slug}
                                          >
                                            <label className="d-flex align-items-center">
                                              <Field
                                                type="checkbox"
                                                className="form-check-input"
                                                name={`userPermissions.${permission.slug}.${perm.permission_slug}`}
                                                value={perm.permission_slug}
                                                checked={
                                                  values?.userPermissions?.[
                                                  permission.slug
                                                  ]?.[perm.permission_slug] ||
                                                  false
                                                }
                                                onChange={(e) => {
                                                  const isChecked =
                                                    e.target.checked;

                                                  const updatedEntityPermissions =
                                                  {
                                                    ...(values
                                                      .userPermissions?.[
                                                      permission.slug
                                                    ] || {}),
                                                    [perm.permission_slug]:
                                                      isChecked,
                                                  };

                                                  const requiresViewActions = [
                                                    "update",
                                                    "delete",
                                                    "status",
                                                  ];
                                                  const isViewAction =
                                                    perm.permission_slug.toLowerCase() ===
                                                    "view";

                                                  if (!isViewAction) {
                                                    if (
                                                      isChecked &&
                                                      requiresViewActions.includes(
                                                        perm.permission_slug.toLowerCase()
                                                      )
                                                    ) {
                                                      updatedEntityPermissions[
                                                        "view"
                                                      ] = true;
                                                    } else if (
                                                      !isChecked &&
                                                      requiresViewActions.includes(
                                                        perm.permission_slug.toLowerCase()
                                                      )
                                                    ) {
                                                      const otherChecked =
                                                        requiresViewActions.some(
                                                          (act) =>
                                                            act !==
                                                            perm.permission_slug.toLowerCase() &&
                                                            values
                                                              .userPermissions?.[
                                                            permission.slug
                                                            ]?.[act]
                                                        );

                                                      if (!otherChecked) {
                                                        const manuallyEnabledView =
                                                          values
                                                            .userPermissions?.[
                                                          permission.slug
                                                          ]?.["view"];
                                                        if (
                                                          !manuallyEnabledView
                                                        ) {
                                                          updatedEntityPermissions[
                                                            "view"
                                                          ] = false;
                                                        }
                                                      }
                                                    }
                                                  } else {
                                                    if (!isChecked) {
                                                      const anyRequiredChecked =
                                                        requiresViewActions.some(
                                                          (act) =>
                                                            values
                                                              .userPermissions?.[
                                                            permission.slug
                                                            ]?.[act]
                                                        );
                                                      if (anyRequiredChecked) {
                                                        return;
                                                      }
                                                    }
                                                  }

                                                  setFieldValue(
                                                    `userPermissions.${permission.slug}`,
                                                    updatedEntityPermissions
                                                  );
                                                }}
                                              />
                                              <span className="ms-2">
                                                {perm.label}
                                              </span>
                                            </label>
                                          </React.Fragment>
                                        ))}
                                    </div>
                                  </div>
                                )
                              );
                            })}
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            variant="btn btn-primary mt-2"
                          >
                            Add User
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default WarehouseAccountAdd;
